import { Flex, Text } from "@hackthenorth/north";
import { Card, Spinner } from "north.js";
import React, { useEffect } from "react";
import styled from "styled-components";

import { Icon } from "src/shared/components";
import { useUserContext } from "src/shared/contexts";
import { useGetFlockGroupLazyQuery } from "src/shared/contexts/NetworkingContext/graphql/getFlockGroup.generated";

const FlockGroup: React.FC = () => {
  const { id: userId } = useUserContext();
  const [getFlockGroup, { data: flockData, loading: flockLoading }] =
    useGetFlockGroupLazyQuery();

  useEffect(() => {
    if (userId) {
      getFlockGroup({
        variables: {
          userId: userId,
        },
      });
    }
  }, [userId, getFlockGroup]);

  const isLoading = flockLoading;
  const flockMembers =
    flockData?.flockGroupSelfWithDetailedGroupMemberInfo || [];

  if (isLoading) {
    return <Spinner />;
  }

  if (flockMembers.length === 0) {
    return null;
  }

  return (
    <FlockSection>
      <Text id="flock" mods="heading h3 bold">
        Your Flock Group
      </Text>
      <SubtitleText>
        Connect with hackers who share similar interests and backgrounds! (
        {flockMembers.length} member{flockMembers.length !== 1 ? "s" : ""})
      </SubtitleText>

      <Flex column>
        {flockMembers.map((member) => (
          <MemberCard key={member.eventAttendeeId}>
            <MemberCardBody>
              <Text mods="bold">{member.name}</Text>
              <Text>{member.email}</Text>
              {member.location && member.location.trim() !== "" && (
                <Text>{member.location}</Text>
              )}
              <MemberSocialLinks>
                {member.instagramLink && (
                  <SocialLink
                    href={member.instagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="instagram" size="h2" />
                  </SocialLink>
                )}
                {member.facebookLink && (
                  <SocialLink
                    href={member.facebookLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="facebook" size="h2" />
                  </SocialLink>
                )}
                {member.twitterLink && (
                  <SocialLink
                    href={member.twitterLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="xlogo" size="h2" />
                  </SocialLink>
                )}
                {member.linkedinLink && (
                  <SocialLink
                    href={member.linkedinLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="linkedin" size="h2" />
                  </SocialLink>
                )}
                {member.discordTag && (
                  <SocialLink as="div">
                    <Icon name="users" size="h2" />
                  </SocialLink>
                )}
              </MemberSocialLinks>
            </MemberCardBody>
          </MemberCard>
        ))}
      </Flex>
    </FlockSection>
  );
};

const FlockSection = styled.div`
  margin: 48px 0;
  @media (max-width: 768px) {
    padding: 0 24px;
  }
`;

const SubtitleText = styled.p`
  margin: 0 0 20px 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 150%;
`;

const MemberCard = styled(Card)`
  margin: 16px 0;
  padding: 16px 24px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06) !important;
  border-radius: 8px !important;
  max-width: 600px;
`;

const MemberCardBody = styled.div`
  display: flex;
  flex-direction: column;
  p {
    overflow-wrap: break-word;
  }
`;

const MemberSocialLinks = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-start;
  margin-top: 8px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
  }

  svg {
    color: #6b7280;
  }
`;

export default FlockGroup;
